/**
 * TasksQuickContext - Controlador único do atalho "Minhas tarefas"
 *
 * Centraliza a abertura do painel, a tarefa destacada no cronômetro e a
 * contagem de tarefas em andamento dos módulos habilitados.
 *
 * @version 1.2.0
 * RELEASE: 2026-10-04 v1.2.0 - Remove o modo flutuante e permite fixar uma execução no cronômetro.
 * RELEASE: 2026-10-04 v1.1.0 - Agrega os módulos e expõe a tarefa em execução mais antiga.
 * RELEASE: 2026-10-04 v1.0.0 - Criação do controlador compartilhado.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useMyActiveTasks } from '@/hooks/useMyActiveTasks';
import { TasksQuickSheet } from '@/components/tasks/quick/TasksQuickSheet';
import { useTasksModuleSettings } from '@/hooks/useTasksModuleSettings';
import type { TaskListItem } from '@/hooks/useTasksPaginated';

interface TasksQuickContextValue {
  /** Indica se o atalho pode aparecer na rota e no tenant atuais. */
  visible: boolean;
  /** Quantidade de tarefas em execução usada no selo dos dois gatilhos. */
  runningCount: number;
  /** Tarefa em andamento mais antiga, usada pelo cronômetro do cabeçalho. */
  runningTask: TaskListItem | null;
  /** ID escolhido explicitamente pelo usuário para o cronômetro. */
  selectedTaskId: string | null;
  /** Abre o painel lateral compartilhado. */
  openSheet: () => void;
  /** Define ou remove a tarefa destacada no cronômetro. */
  selectRunningTask: (taskId: string | null) => void;
}

const TasksQuickContext = createContext<TasksQuickContextValue | null>(null);

export function TasksQuickProvider({ children }: { children: ReactNode }) {
  const { user, currentTenant } = useAuth();
  const { pathname } = useLocation();
  const visible = !!user && !!currentTenant?.tasks_enabled && pathname.startsWith('/tenant');
  const selectedTaskKey = `uaizy:tasks-quick-selected:${user?.id ?? 'anon'}`;
  const [open, setOpen] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const { retentionEnabled, legalEnabled } = useTasksModuleSettings();
  const { data: normalTasks = [], isLoading: normalLoading } = useMyActiveTasks('normal', visible);
  const { data: retentionTasks = [], isLoading: retentionLoading } = useMyActiveTasks('retention', visible && retentionEnabled);
  const { data: legalTasks = [], isLoading: legalLoading } = useMyActiveTasks('legal', visible && legalEnabled);

  // Considera todos os módulos habilitados e escolhe a execução iniciada há mais tempo.
  const runningTasks = useMemo(() => (
    [...normalTasks, ...retentionTasks, ...legalTasks]
      .filter((task) => task.status === 'in_progress' && !task.is_paused && !!task.started_at)
  ), [legalTasks, normalTasks, retentionTasks]);
  const runningTask = useMemo(() => (
    runningTasks.find((task) => task.id === selectedTaskId) ?? [...runningTasks].sort((first, second) => (
      new Date(first.started_at ?? 0).getTime() - new Date(second.started_at ?? 0).getTime()
    ))[0] ?? null
  ), [runningTasks, selectedTaskId]);

  // Restaura a execução escolhida pelo usuário para o cronômetro.
  useEffect(() => {
    if (!user?.id) {
      setSelectedTaskId(null);
      return;
    }
    try {
      setSelectedTaskId(localStorage.getItem(selectedTaskKey));
    } catch {
      setSelectedTaskId(null);
    }
  }, [selectedTaskKey, user?.id]);

  const selectRunningTask = (taskId: string | null) => {
    setSelectedTaskId(taskId);
    try {
      if (taskId) localStorage.setItem(selectedTaskKey, taskId);
      else localStorage.removeItem(selectedTaskKey);
    } catch {
      // A escolha continua válida nesta sessão quando o navegador bloqueia storage.
    }
  };

  // Descarta a escolha quando a tarefa deixa de estar efetivamente em andamento.
  useEffect(() => {
    const loading = normalLoading || (retentionEnabled && retentionLoading) || (legalEnabled && legalLoading);
    if (!loading && selectedTaskId && !runningTasks.some((task) => task.id === selectedTaskId)) {
      selectRunningTask(null);
    }
  }, [legalEnabled, legalLoading, normalLoading, retentionEnabled, retentionLoading, runningTasks, selectedTaskId]);

  const value = useMemo<TasksQuickContextValue>(() => ({
    visible,
    runningCount: runningTasks.length,
    runningTask,
    selectedTaskId,
    openSheet: () => setOpen(true),
    selectRunningTask,
  }), [runningTask, runningTasks.length, selectedTaskId, visible]);

  return (
    <TasksQuickContext.Provider value={value}>
      {children}
      {visible && (
        <TasksQuickSheet
          open={open}
          onOpenChange={setOpen}
          selectedTaskId={selectedTaskId}
          onSelectTask={selectRunningTask}
        />
      )}
    </TasksQuickContext.Provider>
  );
}

export function useTasksQuick() {
  return useContext(TasksQuickContext);
}